import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PaymentLookup, PayAuthUser, PaymentResult } from '../models/payment.model';

const API = `${environment.apiUrl}/api/v3/api`;
const TOKEN_KEY = 'arp_pay_token';
const USER_KEY  = 'arp_pay_user';

@Injectable({ providedIn: 'root' })
export class PaymentService {
  private http = inject(HttpClient);

  // ── Auth state ────────────────────────────────────────────────────────────

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getUser(): PayAuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  login(email: string, password: string): Observable<PayAuthUser> {
    return this.http.post<any>(`${API}/auth/login`, { email, password }).pipe(
      map(r => this._storeSession(r, email))
    );
  }

  register(name: string, email: string, password: string): Observable<PayAuthUser> {
    return this.http.post<any>(`${API}/auth/register`, { name, email, password }).pipe(
      map(r => this._storeSession(r, email))
    );
  }

  private _storeSession(r: any, fallbackEmail: string): PayAuthUser {
    const token = r.token ?? r.data?.token ?? r.access_token;
    const user: PayAuthUser = {
      id:    r.user?.id    ?? r.data?.user?.id,
      name:  r.user?.name  ?? r.data?.user?.name  ?? fallbackEmail,
      email: r.user?.email ?? r.data?.user?.email ?? fallbackEmail,
    };
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  // ── Payment API ───────────────────────────────────────────────────────────

  lookup(ref: string): Observable<PaymentLookup> {
    return this.http
      .get<any>(`${API}/public/payment-lookup`, { params: { ref } })
      .pipe(map(r => r.data));
  }

  pay(payload: {
    type: 'application' | 'infraction';
    entity_id: number;
    payment_method: string;
    phone_number?: string;
    payment_reference?: string;
  }): Observable<PaymentResult> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.getToken()}`,
    });
    return this.http
      .post<any>(`${API}/public/pay`, payload, { headers })
      .pipe(map(r => r.data));
  }
}
